
import useScrollReveal from '../../../hooks/useScrollReveal';

interface Review {
  id: number;
  author: string;
  rating: number;
  date: string;
  text: string;
  verified: boolean;
}

interface ProductReviewsProps {
  reviews: Review[];
  rating: number;
  reviewCount: number;
}

export default function ProductReviews({ reviews, rating, reviewCount }: ProductReviewsProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: listRef, isVisible: listVisible } = useScrollReveal({ threshold: 0.08 });

  return (
    <section className="py-20 px-6 bg-zinc-950">
      <div className="max-w-5xl mx-auto">
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-600 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-4xl font-black tracking-tight mb-4">WHAT USERS SAY</h2>
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`ri-star-${i < Math.floor(rating) ? 'fill' : i < rating ? 'half-fill' : 'line'} text-amber-400 text-xl`}
                ></i>
              ))}
            </div>
            <span className="text-lg font-bold">{rating}</span>
            <span className="text-gray-400">based on {reviewCount} reviews</span>
          </div>
        </div>

        <div ref={listRef} className="space-y-6">
          {reviews.map((review, index) => (
            <div
              key={review.id}
              className={`bg-zinc-900 rounded-xl p-6 relative transition-all duration-600 ease-out ${
                listVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: listVisible ? `${index * 120}ms` : '0ms' }}
            >
              <div className="absolute top-6 right-6 text-zinc-800">
                <i className="ri-double-quotes-r text-4xl"></i>
              </div>
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`ri-star-${i < review.rating ? 'fill' : 'line'} text-amber-400 text-sm`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-4 pr-10">
                &ldquo;{review.text}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-500/15 rounded-full">
                    <span className="text-sm font-bold text-teal-400">
                      {review.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{review.author}</span>
                      {review.verified && (
                        <span className="flex items-center gap-1 text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">
                          <i className="ri-verified-badge-fill text-xs"></i>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-gray-500">{review.date}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
